import { AgentsAccess } from 'api/Cerebro/orgScoped/agentsTypes'
import {
    SELLER_CENTRAL_READ,
    SOV_READ,
    VENDOR_CENTRAL_READ,
} from 'const/featurePermissions'
import {
    orgHasPermissions,
    userHasDashboardsOnlyPermissions,
    userHasManageLabelsPermissions,
} from 'helpers/featurePermissions'
import { FeatureFlagsSet } from 'hooks/useFeatureFlags'
import { GatedFeatures } from 'hooks/useGatedFeatures'
import { UserPermission } from 'types'

import {
    ResolvedWhatsNewEntry,
    WhatsNewAvailability,
    WhatsNewEntry,
} from './whatsNewTypes'

const hasAvailability = (
    availability: WhatsNewAvailability,
    gatedFeatures: GatedFeatures,
    featurePermissions: UserPermission[],
    agentsAccess?: AgentsAccess
): boolean => {
    switch (availability) {
        case 'agents':
            return (
                agentsAccess !== 'none' &&
                !userHasDashboardsOnlyPermissions(featurePermissions)
            )
        case 'buyBoxPerformance':
            return gatedFeatures.buyBoxPerformance
        case 'dspIntegration':
            return gatedFeatures.dspIntegration
        case 'executiveHomepage':
            return gatedFeatures.executiveHomepage
        case 'manageLabels':
            return userHasManageLabelsPermissions(featurePermissions)
        case 'sellerCentral':
            return orgHasPermissions(featurePermissions, SELLER_CENTRAL_READ)
        case 'sellerCompliance':
            return gatedFeatures.sellerCompliance
        case 'sellingPartnerIntegration':
            return gatedFeatures.sellingPartnerIntegration
        case 'shareOfVoice':
            return orgHasPermissions(featurePermissions, SOV_READ)
        case 'vendorCentral':
            return orgHasPermissions(featurePermissions, VENDOR_CENTRAL_READ)
        case 'newSegmentExperience':
            return gatedFeatures.newSegmentExperience
    }
}

export const filterWhatsNewEntries = (
    entries: WhatsNewEntry[],
    featureFlags: FeatureFlagsSet,
    gatedFeatures: GatedFeatures,
    featurePermissions: UserPermission[],
    agentsAccess?: AgentsAccess
): ResolvedWhatsNewEntry[] =>
    entries.flatMap((entry): ResolvedWhatsNewEntry[] => {
        const hasEnabledFeatureFlag =
            !entry.featureFlag || featureFlags[entry.featureFlag]

        const hasAllAvailability =
            !entry.availability?.all?.length ||
            entry.availability.all.every((availability) =>
                hasAvailability(
                    availability,
                    gatedFeatures,
                    featurePermissions,
                    agentsAccess
                )
            )

        const hasAnyAvailability =
            !entry.availability?.any?.length ||
            entry.availability.any.some((availability) =>
                hasAvailability(
                    availability,
                    gatedFeatures,
                    featurePermissions,
                    agentsAccess
                )
            )

        if (hasEnabledFeatureFlag && hasAllAvailability && hasAnyAvailability) {
            return [{ ...entry, accessStatus: 'available' }]
        }

        return entry.missingPermissionBehavior === 'hide'
            ? []
            : [{ ...entry, accessStatus: 'missingPermission' }]
    })
